"use client";

import { useSearchParams, useRouter } from "next/navigation";
import { useState, Suspense } from "react";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Link from "next/link";
import { AlertCircle } from "lucide-react";
import { Locale } from "@/i18n-config";
import { defineLocaleRecord } from "@/i18n-config";
import { getAppCopy } from "@/content/app-copy";
import { getAuthSocialCopy } from "@/content/auth-social-copy";
import { buildAuthCallbackUrl, resolveSafeNextPath } from "@/lib/auth/oauth";
import { SocialProviderIcon } from "@/components/auth/social-provider-icon";

type LoginBridgeCopy = {
    title: string;
    subtitle: string;
    backgroundStatement: string;
    anchor: string;
};

const loginBridgeCopyByLocale = defineLocaleRecord<LoginBridgeCopy>({
    en: {
        title: "Continue from where you stopped",
        subtitle: "Your next clear step is waiting.",
        backgroundStatement: "One step. No noise.",
        anchor: "You do not need to figure everything out again.",
    },
    fr: {
        title: "Continuez la ou vous vous etes arrete",
        subtitle: "Votre prochaine etape claire vous attend.",
        backgroundStatement: "Un pas. Pas de bruit.",
        anchor: "Vous n'avez pas besoin de tout redecouvrir.",
    },
    ru: {
        title: "Продолжайте с того места, где остановились",
        subtitle: "Ваш следующий ясный шаг уже ждет.",
        backgroundStatement: "Один шаг. Без шума.",
        anchor: "Не нужно заново во всем разбираться.",
    },
    uk: {
        title: "Продовжуйте з того місця, де зупинились",
        subtitle: "Ваш наступний чіткий крок вже чекає.",
        backgroundStatement: "Один крок. Без шуму.",
        anchor: "Не потрібно знову в усьому розбиратися.",
    },
    de: {
        title: "Mach dort weiter, wo du aufgehort hast",
        subtitle: "Dein klarer naechster Schritt wartet schon.",
        backgroundStatement: "Ein Schritt. Kein Rauschen.",
        anchor: "Du musst nicht wieder alles neu herausfinden.",
    },
    es: {
        title: "Continua donde lo dejaste",
        subtitle: "Tu siguiente paso claro ya te espera.",
        backgroundStatement: "Un paso. Sin ruido.",
        anchor: "No necesitas volver a descifrarlo todo.",
    },
    it: {
        title: "Continua da dove ti sei fermato",
        subtitle: "Il tuo prossimo passo chiaro ti aspetta.",
        backgroundStatement: "Un passo. Nessun rumore.",
        anchor: "Non devi capire tutto da capo.",
    },
});

function LoginForm({ lang }: { lang: Locale }) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const copy = getAppCopy(lang).auth.login;
    const continuityCopy = loginBridgeCopyByLocale[lang];
    const socialCopy = getAuthSocialCopy(lang);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [oauthLoading, setOauthLoading] = useState<"google" | "github" | null>(null);
    const [error, setError] = useState<string | null>(searchParams.get("error"));
    const requestedNext = searchParams.get("next");
    const safeNext = resolveSafeNextPath(requestedNext, `/${lang}/progress`);
    const supabase = createClient();

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (error) {
            setError(error.message);
            setIsLoading(false);
        } else {
            router.push(safeNext);
            router.refresh();
        }
    };

    const handleOAuthLogin = async (provider: "google" | "github") => {
        setError(null);
        setOauthLoading(provider);

        const redirectTo = buildAuthCallbackUrl(window.location.origin, safeNext);
        const { error: oauthError } = await supabase.auth.signInWithOAuth({
            provider,
            options: {
                redirectTo,
            },
        });

        if (oauthError) {
            setError(oauthError.message);
            setOauthLoading(null);
        }
    };

    return (
        <form onSubmit={handleLogin} className="space-y-6 relative z-10">
            {error && (
                <div className="rounded-xl border border-[#c24f45]/25 bg-[#fff1ef] p-3 text-sm text-[#8b2f28] flex items-center gap-2">
                    <AlertCircle size={16} /> {error}
                </div>
            )}

            <div className="space-y-4">
                <Input
                    id="email"
                    type="email"
                    placeholder={copy.emailPlaceholder}
                    label={copy.emailLabel}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className="border-[#d7c9b2] bg-[#fffdf8] text-[#1f2a27] placeholder:text-[#8ea099] focus-visible:ring-[#0f766e]"
                />
                <Input
                    id="password"
                    type="password"
                    placeholder={copy.passwordPlaceholder}
                    label={copy.passwordLabel}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="border-[#d7c9b2] bg-[#fffdf8] text-[#1f2a27] placeholder:text-[#8ea099] focus-visible:ring-[#0f766e]"
                />
            </div>

            <div className="text-right text-sm">
                <Link href={`/${lang}/forgot-password`} className="font-medium text-[#0f766e] hover:text-[#0d5d57]">
                    {copy.forgotPassword}
                </Link>
            </div>

            <div className="grid gap-3">
                <Button
                    type="button"
                    variant="outline"
                    disabled={isLoading || oauthLoading !== null}
                    onClick={() => void handleOAuthLogin("google")}
                    className="w-full border-[#d7c9b2] bg-[#fffdf8] text-[#1f2a27] hover:bg-[#f8f1e4]"
                >
                    <SocialProviderIcon provider="google" className="mr-2 h-4 w-4" />
                    {oauthLoading === "google" ? socialCopy.connecting : socialCopy.continueWithGoogle}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    disabled={isLoading || oauthLoading !== null}
                    onClick={() => void handleOAuthLogin("github")}
                    className="w-full border-[#d7c9b2] bg-[#fffdf8] text-[#1f2a27] hover:bg-[#f8f1e4]"
                >
                    <SocialProviderIcon provider="github" className="mr-2 h-4 w-4" />
                    {oauthLoading === "github" ? socialCopy.connecting : socialCopy.continueWithGithub}
                </Button>
            </div>

            <Button
                type="submit"
                disabled={isLoading || oauthLoading !== null}
                className="w-full rounded-xl border border-[#0f766e]/30 bg-[#0f766e] text-white shadow-[0_8px_0_rgba(14,74,69,0.35)] hover:translate-y-[1px] hover:bg-[#0f6a63] hover:shadow-[0_6px_0_rgba(14,74,69,0.35)]"
                size="lg"
            >
                {isLoading ? copy.submitting : copy.submit}
            </Button>

            <p className="text-center text-xs text-[#556a64]">{continuityCopy.anchor}</p>

            <div className="text-center text-sm">
                <span className="text-[#5f726d]">{copy.noAccount} </span>
                <Link
                    href={`/${lang}/signup?next=${encodeURIComponent(safeNext)}`}
                    className="font-medium text-[#0f766e] hover:text-[#0d5d57]"
                >
                    {copy.createAccount}
                </Link>
            </div>
        </form>
    );
}

export function LoginView({ lang }: { lang: Locale }) {
    const copy = getAppCopy(lang).auth.login;
    const continuityCopy = loginBridgeCopyByLocale[lang];

    return (
        <section className="relative min-h-screen overflow-hidden bg-[#f4efe3] px-4 pb-12 pt-24 md:pb-16 md:pt-28">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_10%_12%,rgba(180,83,9,0.14),transparent_36%),radial-gradient(circle_at_84%_16%,rgba(15,118,110,0.14),transparent_36%)]" />
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(29,38,34,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(29,38,34,0.05)_1px,transparent_1px)] bg-[size:26px_26px] [mask-image:radial-gradient(ellipse_88%_72%_at_50%_0%,#000_72%,transparent_100%)] opacity-25" />

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
                <p className="select-none text-center text-[clamp(2.4rem,11vw,8rem)] font-bold tracking-tight text-[#1b2220]/[0.06]">
                    {continuityCopy.backgroundStatement}
                </p>
            </div>

            <div className="container relative flex min-h-[calc(100vh-9rem)] items-center justify-center">
                <div className="w-full max-w-md rounded-3xl border border-[#d8cab2] bg-[#fffaf1] p-8 shadow-[0_12px_28px_rgba(34,47,42,0.08)]">
                    <div className="mb-8 text-center">
                        <h1 className="text-3xl font-bold text-[#1b2220] md:text-4xl">{continuityCopy.title}</h1>
                        <p className="mt-3 text-[#3e5350]">{continuityCopy.subtitle}</p>
                    </div>

                    <Suspense fallback={<div className="py-10 text-center text-[#60726c]">{copy.loadingForm}</div>}>
                        <LoginForm lang={lang} />
                    </Suspense>
                </div>
            </div>
        </section>
    );
}
